// Notification API service for backend integration
import { API_CONFIG } from '../constants/config';

// Notification types based on backend endpoints
export interface NotificationRequest {
  userId?: string;
  message: string;
  type: 'booking_confirmation' | 'booking_update' | 'booking_cancellation' | 'system_alert' | 'reminder';
  recipientEmail?: string;
  recipientPhone?: string;
  metadata?: Record<string, unknown>;
}

export interface NotificationResponse {
  id: string;
  userId?: string;
  message: string;
  type: string;
  status: 'sent' | 'delivered' | 'failed' | 'pending';
  createdAt: string;
  sentAt?: string;
  metadata?: Record<string, unknown>;
}

export interface NotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  bookingConfirmations: boolean;
  bookingUpdates: boolean;
  systemAlerts: boolean;
  reminderNotifications: boolean;
}

class NotificationApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend CORS configuration)
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? '' : (import.meta.env.VITE_API_URL || 'https://admin.grandpittu.lk');
    const url = `${baseUrl}${API_CONFIG.SLUG}${endpoint}`;

    // Headers for the request - no need for Authorization header, cookies are sent automatically
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await fetch(url, {
        ...options,
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        signal: AbortSignal.timeout(API_CONFIG.TIMEOUT),
      });

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} - ${response.statusText}`;

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If JSON parsing fails, use the original error message
        }

        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return await response.text() as T;
    } catch (error) {

      // Retry logic for network errors
      if (retryCount < 2 && error instanceof TypeError) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return this.makeRequest<T>(endpoint, options, retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Create/Update notification (Backend endpoint)
   * Endpoint: POST /notification/{id}
   */
  async createNotification(id: number, request?: Partial<NotificationRequest>): Promise<string> {
    return this.makeRequest<string>(`/notification/${id}`, {
      method: 'POST',
      body: request ? JSON.stringify(request) : undefined,
    });
  }

  /**
   * Get all notifications by period (Backend endpoint)
   * Endpoint: GET /notification/getAllByPeriod
   */
  async getAllNotificationsByPeriod(): Promise<NotificationResponse[]> {
    return this.makeRequest<NotificationResponse[]>('/notification/getAllByPeriod');
  }

  /**
   * Legacy method - Send a notification (Custom implementation)
   * Endpoint: POST /notifications/send (Custom endpoint, not in backend docs)
   */
  async sendNotification(request: NotificationRequest): Promise<NotificationResponse> {
    return this.makeRequest<NotificationResponse>('/notifications/send', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Send booking confirmation notification
   * Convenience method for booking confirmations
   */
  async sendBookingConfirmation(
    customerEmail: string,
    customerName: string,
    bookingDetails: {
      bookingId: string;
      date: string;
      time: string;
      tableNames: string[];
      guestCount: number;
    }
  ): Promise<NotificationResponse> {
    const message = `Dear ${customerName}, your reservation for ${bookingDetails.guestCount} guests on ${bookingDetails.date} at ${bookingDetails.time} (Tables: ${bookingDetails.tableNames.join(', ')}) has been confirmed. Booking ID: ${bookingDetails.bookingId}`;

    return this.sendNotification({
      message,
      type: 'booking_confirmation',
      recipientEmail: customerEmail,
      metadata: {
        customerName,
        ...bookingDetails
      }
    });
  }

  /**
   * Send booking update notification
   * For status changes, modifications, etc.
   */
  async sendBookingUpdate(
    customerEmail: string,
    customerName: string,
    bookingId: string,
    updateMessage: string,
    updateType: 'status_change' | 'modification' | 'reminder'
  ): Promise<NotificationResponse> {
    const message = `Dear ${customerName}, ${updateMessage} Booking ID: ${bookingId}`;

    return this.sendNotification({
      message,
      type: 'booking_update',
      recipientEmail: customerEmail,
      metadata: {
        bookingId,
        customerName,
        updateType
      }
    });
  }

  /**
   * Send booking cancellation notification
   */
  async sendBookingCancellation(
    customerEmail: string,
    customerName: string,
    bookingId: string,
    reason?: string
  ): Promise<NotificationResponse> {
    const reasonText = reason ? ` Reason: ${reason}` : '';
    const message = `Dear ${customerName}, your reservation (Booking ID: ${bookingId}) has been cancelled.${reasonText} We apologize for any inconvenience.`;

    return this.sendNotification({
      message,
      type: 'booking_cancellation',
      recipientEmail: customerEmail,
      metadata: {
        bookingId,
        customerName,
        reason
      }
    });
  }

  /**
   * Send SMS notification
   * Endpoint: POST /notifications/sms
   */
  async sendSMS(
    phoneNumber: string,
    message: string,
    type: string = 'general'
  ): Promise<NotificationResponse> {
    return this.makeRequest<NotificationResponse>('/notifications/sms', {
      method: 'POST',
      body: JSON.stringify({
        recipientPhone: phoneNumber,
        message,
        type
      }),
    });
  }

  /**
   * Get notification history for a user
   * Endpoint: GET /notifications/history/{userId}
   */
  async getNotificationHistory(
    userId: string,
    page = 0,
    size = 20
  ): Promise<{
    content: NotificationResponse[];
    totalElements: number;
    totalPages: number;
    pageNumber: number;
    pageSize: number;
  }> {
    return this.makeRequest<{
      content: NotificationResponse[];
      totalElements: number;
      totalPages: number;
      pageNumber: number;
      pageSize: number;
    }>(`/notifications/history/${userId}?page=${page}&size=${size}`);
  }

  /**
   * Get notification settings for a user
   * Endpoint: GET /notifications/settings/{userId}
   */
  async getNotificationSettings(userId: string): Promise<NotificationSettings> {
    return this.makeRequest<NotificationSettings>(`/notifications/settings/${userId}`);
  }

  /**
   * Update notification settings for a user
   * Endpoint: PUT /notifications/settings/{userId}
   */
  async updateNotificationSettings(
    userId: string,
    settings: Partial<NotificationSettings>
  ): Promise<NotificationSettings> {
    return this.makeRequest<NotificationSettings>(`/notifications/settings/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  /**
   * Send system alert to all admins
   * Endpoint: POST /notifications/system-alert
   */
  async sendSystemAlert(
    alertType: 'error' | 'warning' | 'info',
    title: string,
    message: string,
    metadata?: Record<string, unknown>
  ): Promise<NotificationResponse[]> {
    return this.makeRequest<NotificationResponse[]>('/notifications/system-alert', {
      method: 'POST',
      body: JSON.stringify({
        type: 'system_alert',
        message: `${title}: ${message}`,
        metadata: {
          alertType,
          title,
          ...metadata
        }
      }),
    });
  }

  /**
   * Send reminder notifications
   * For upcoming reservations, follow-ups, etc.
   */
  async sendReminder(
    customerEmail: string,
    customerName: string,
    reminderType: 'upcoming_reservation' | 'feedback_request' | 'special_offer',
    message: string,
    scheduledFor?: string
  ): Promise<NotificationResponse> {
    return this.sendNotification({
      message: `Dear ${customerName}, ${message}`,
      type: 'reminder',
      recipientEmail: customerEmail,
      metadata: {
        customerName,
        reminderType,
        scheduledFor
      }
    });
  }

  /**
   * Get notification status
   * Endpoint: GET /notifications/{notificationId}/status
   */
  async getNotificationStatus(notificationId: string): Promise<{
    id: string;
    status: 'sent' | 'delivered' | 'failed' | 'pending';
    sentAt?: string;
    deliveredAt?: string;
    failureReason?: string;
  }> {
    return this.makeRequest<{
      id: string;
      status: 'sent' | 'delivered' | 'failed' | 'pending';
      sentAt?: string;
      deliveredAt?: string;
      failureReason?: string;
    }>(`/notifications/${notificationId}/status`);
  }

  /**
   * Mark notification as read
   * Endpoint: PUT /notifications/{notificationId}/read
   */
  async markAsRead(notificationId: string): Promise<void> {
    await this.makeRequest<void>(`/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  }

  /**
   * Delete notification
   * Endpoint: DELETE /notifications/{notificationId}
   */
  async deleteNotification(notificationId: string): Promise<void> {
    await this.makeRequest<void>(`/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const notificationApi = new NotificationApiService();
export default notificationApi;